/** Вкладка «Проверки» (`tab=checks`): текущее значение каждой проверки кабинета (`/checks`,
 *  fail → warn → ok; день — последний день агрегата проверки, контракт v1.3; «давний» — раньше дня
 *  хранения кабинета, у проверок источников — раньше дня сборки) с раскрытием объектов (поставка →
 *  карточка поставки, nm → карточка оффера по `offer_keys` бэкенда, склад и источник — текстом) и
 *  полоса истории статусов по дням (`/checks/history`, только агрегаты кабинета). Значения — как
 *  отдала витрина. */
import { ChevronDown, ChevronRight } from "lucide-react";
import React, { useState } from "react";

import {
  WB_ALL,
  type WbCheck,
  type WbCheckHistoryRow,
  type WbCheckObject,
  type WbCheckValues,
  type WbChecksHistory,
} from "@/api/wbRegions";
import { cn } from "@/lib/utils";
import { fmtWhen, int } from "../ozon-analytics/format";
import { thClass } from "../ozon-analytics/ui";
import { ErrorBox, Loading, Missing } from "./cells";
import type { useWbChecks, useWbChecksHistory } from "./queries";
import { SUPPLY_ID_RE, type CardRef } from "./state";
import { CHECK_STATUS_STYLE, historyStrip, OBJECT_KIND_LABELS, sortChecks } from "./supplyFormat";

/** Опорный день «давности» проверки — в подсказке. */
const REFERENCE_LABEL: Record<WbCheck["reference"], string> = {
  fbo_stock_date: "дня хранения кабинета",
  build_day: "дня сборки",
};

/** Число витрины: целое — «1 234», дробное — до 2 знаков; null — null. */
function num(v: number | null): string | null {
  if (v === null) return null;
  if (Number.isInteger(v)) return int(v);
  return v.toLocaleString("ru-RU", { maximumFractionDigits: 2 });
}

function Num({ v, suffix = "" }: { v: number | null; suffix?: string }): React.ReactElement {
  const t = num(v);
  return t === null ? <Missing text="—" /> : <>{t + suffix}</>;
}

export function StatusBadge({ status }: { status: WbCheck["status"] }): React.ReactElement {
  const st = CHECK_STATUS_STYLE[status];
  return (
    <span className={cn("rounded px-1.5 py-0.5 text-[11px] font-medium", st.cls)} data-testid="check-status" data-status={status}>
      {st.label}
    </span>
  );
}

function CheckName({ code, label }: { code: string; label: string | null }): React.ReactElement {
  if (label) {
    return (
      <span title={code} data-testid="check-label">
        {label}
      </span>
    );
  }
  return (
    <span data-testid="check-label">
      <code className="text-[11px]">{code}</code>{" "}
      <span className="rounded bg-amber-100 px-1 text-[10px] text-amber-900 dark:bg-amber-950 dark:text-amber-300" data-testid="no-label">
        без подписи
      </span>
    </span>
  );
}

function ValuesCells({ v }: { v: WbCheckValues }): React.ReactElement {
  return (
    <>
      <td className="px-2 py-1 text-right tabular-nums" data-testid="check-values">
        <Num v={v.value} /> · <Num v={v.expected} />
        <div className="text-[11px] text-muted-foreground">
          Δ <Num v={v.diff_pct} suffix="%" /> / доля <Num v={v.share_pct} suffix="%" />
        </div>
      </td>
      <td className="px-2 py-1 text-right tabular-nums" data-testid="check-threshold">
        <Num v={v.threshold_warn} /> / <Num v={v.threshold_fail} />
        <div className="text-[11px] text-muted-foreground">{v.unit ?? "ед. не указаны"}</div>
      </td>
    </>
  );
}

function ObjectRef({
  o,
  legal,
  onOpenCard,
}: {
  o: WbCheckObject;
  legal: string;
  onOpenCard: (c: CardRef) => void;
}): React.ReactElement {
  const kind = OBJECT_KIND_LABELS[o.object_kind] ?? o.object_kind;
  const id = o.object_id ?? "без id";
  if (o.object_kind === "supply") {
    if (o.object_id && SUPPLY_ID_RE.test(o.object_id)) {
      return (
        <button
          type="button"
          className="text-primary hover:underline"
          data-testid="object-supply-link"
          onClick={() => onOpenCard({ kind: "supply", legal, supplyId: Number(o.object_id) })}
        >
          {kind} {id}
        </button>
      );
    }
    return (
      <span>
        {kind} {id} <span className="text-amber-700 dark:text-amber-400">(номер не распознан)</span>
      </span>
    );
  }
  if (o.offer_keys !== null) {
    if (o.offer_keys.length === 0) {
      return (
        <span data-testid="object-no-offer">
          {kind} {id} <span className="text-muted-foreground">— нет в витрине сейчас</span>
        </span>
      );
    }
    return (
      <span className="inline-flex flex-wrap gap-1">
        {o.offer_keys.map((k, i) => (
          <button
            key={k}
            type="button"
            className="text-primary hover:underline"
            data-testid="object-offer-link"
            onClick={() => onOpenCard({ kind: "offer", legal, offerKey: k })}
          >
            {kind} {id}
            {o.offer_keys!.length > 1 ? ` (оффер ${i + 1})` : ""}
          </button>
        ))}
      </span>
    );
  }
  return (
    <span data-testid="object-text">
      {kind} {id}
    </span>
  );
}

function CheckRow({
  c,
  showLegal,
  onOpenCard,
}: {
  c: WbCheck;
  showLegal: boolean;
  onOpenCard: (c: CardRef) => void;
}): React.ReactElement {
  const [open, setOpen] = useState(false);
  const cols = 6;
  return (
    <>
      <tr className="border-t align-top" data-testid="check-row" data-code={c.check_code}>
        <td className="px-2 py-1">
          {showLegal && <div className="text-[10px] text-muted-foreground">{c.legal_name}</div>}
          <CheckName code={c.check_code} label={c.label} />
          {c.objects_count > 0 && (
            <button
              type="button"
              aria-expanded={open}
              className="ml-1 inline-flex items-center text-[11px] text-muted-foreground hover:underline"
              onClick={() => setOpen(!open)}
              data-testid="objects-toggle"
            >
              {open ? <ChevronDown className="size-3" aria-hidden /> : <ChevronRight className="size-3" aria-hidden />}
              объекты: {c.objects_count}
              {c.objects_by_status.fail > 0 && ` (fail ${c.objects_by_status.fail})`}
              {c.objects_by_status.warn > 0 && ` (warn ${c.objects_by_status.warn})`}
            </button>
          )}
        </td>
        <td className="px-2 py-1">
          <StatusBadge status={c.status} />
          {c.status_from === "objects" && (
            <div
              className="text-[10px] text-muted-foreground"
              title={`агрегата кабинета нет — статус худший из объектов за день агрегата ${c.base_check_code}`}
              data-testid="status-from-objects"
            >
              {c.objects_count === 0 ? "по объектам: нарушений нет" : "по объектам"}
            </div>
          )}
        </td>
        {c.aggregate ? (
          <ValuesCells v={c.aggregate} />
        ) : (
          <td colSpan={2} className="px-2 py-1 text-muted-foreground" data-testid="no-aggregate">
            {c.objects_count === 0
              ? "агрегата кабинета нет; строк объектов за день нет — нарушителей нет"
              : "агрегата кабинета нет — значения у объектов"}
          </td>
        )}
        <td className="px-2 py-1 whitespace-nowrap" data-testid="check-date">
          {fmtWhen(c.date)}
          {c.stale && (
            <div
              className="text-[10px] text-amber-700 dark:text-amber-400"
              title={`текущее значение за ${fmtWhen(c.date)} — раньше ${REFERENCE_LABEL[c.reference]} ${fmtWhen(c.reference_date)}`}
              data-testid="old-day"
            >
              давний день
            </div>
          )}
        </td>
        <td className="px-2 py-1 text-muted-foreground">{c.aggregate?.details ?? "—"}</td>
      </tr>
      {open && (
        <tr className="bg-muted/20" data-testid="check-objects">
          <td colSpan={cols} className="px-2 py-1">
            <ul className="space-y-0.5 pl-4">
              {c.objects.map((o) => (
                <li key={`${o.object_kind}:${o.object_id ?? ""}`} className="flex flex-wrap items-center gap-2" data-testid="check-object">
                  <StatusBadge status={o.status} />
                  <ObjectRef o={o} legal={c.legal_name} onOpenCard={onOpenCard} />
                  <span className="tabular-nums text-muted-foreground">
                    {o.object_kind === "warehouse" && "сейчас "}
                    <Num v={o.value} /> · {o.object_kind === "warehouse" && "прошлый снимок "}
                    <Num v={o.expected} />
                    {o.diff_pct !== null && <> · Δ {num(o.diff_pct)}%</>}
                  </span>
                  {o.details && <span className="text-muted-foreground">{o.details}</span>}
                </li>
              ))}
            </ul>
          </td>
        </tr>
      )}
    </>
  );
}

function cellTitle(r: WbCheckHistoryRow): string {
  const t = (v: number | null): string => num(v) ?? "—";
  return [
    `${fmtWhen(r.date)}: ${r.status}`,
    `наше ${t(r.value)} · ожидалось ${t(r.expected)}`,
    `доля ${t(r.share_pct)}% · Δ ${t(r.diff_pct)}%`,
    `порог warn ${t(r.threshold_warn)} / fail ${t(r.threshold_fail)} (${r.unit ?? "ед. не указаны"})`,
    r.details,
  ]
    .filter(Boolean)
    .join("\n");
}

function HistoryStripView({
  data,
  order,
  showLegal,
}: {
  data: WbChecksHistory;
  order: string[];
  showLegal: boolean;
}): React.ReactElement {
  const strip = historyStrip(data.rows, data.labels, order);
  if (strip.rows.length === 0) {
    return <div className="text-xs text-muted-foreground">Истории агрегатов проверок нет.</div>;
  }
  return (
    <div className="overflow-x-auto rounded-md border p-2" data-testid="checks-history">
      <div className="mb-1 flex justify-between pl-[260px] text-[10px] text-muted-foreground">
        <span>{fmtWhen(strip.dates[0]!)}</span>
        <span>{fmtWhen(strip.dates[strip.dates.length - 1]!)}</span>
      </div>
      <div className="space-y-0.5">
        {strip.rows.map((row) => (
          <div key={row.key} className="flex items-center" data-testid="history-row" data-code={row.code}>
            <div className="w-[260px] shrink-0 truncate pr-2 text-[11px]" title={row.code}>
              {showLegal && <span className="text-muted-foreground">{row.legal}: </span>}
              <CheckName code={row.code} label={row.label} />
            </div>
            <div className="flex gap-px">
              {strip.dates.map((d) => {
                const r = row.byDate.get(d);
                return r ? (
                  <span
                    key={d}
                    className={cn("inline-block h-4 w-1.5 rounded-[1px]", CHECK_STATUS_STYLE[r.status].cell)}
                    title={cellTitle(r)}
                    data-testid="history-cell"
                    data-date={d}
                    data-status={r.status}
                  />
                ) : (
                  <span
                    key={d}
                    className="inline-block h-4 w-1.5 rounded-[1px] bg-muted"
                    title={`${fmtWhen(d)}: строки агрегата за день нет`}
                    data-testid="history-cell"
                    data-date={d}
                    data-status="none"
                  />
                );
              })}
            </div>
          </div>
        ))}
      </div>
      <div className="mt-2 flex flex-wrap gap-3 text-[10px] text-muted-foreground">
        {(["fail", "warn", "ok"] as const).map((s) => (
          <span key={s} className="inline-flex items-center gap-1">
            <span className={cn("inline-block h-3 w-1.5 rounded-[1px]", CHECK_STATUS_STYLE[s].cell)} /> {s}
          </span>
        ))}
        <span className="inline-flex items-center gap-1">
          <span className="inline-block h-3 w-1.5 rounded-[1px] bg-muted" /> нет строки за день
        </span>
        <span>· наведение — значение, порог, детали</span>
      </div>
    </div>
  );
}

export function ChecksTab({
  legal,
  checks,
  history,
  onOpenCard,
}: {
  legal: string;
  checks: ReturnType<typeof useWbChecks>;
  history: ReturnType<typeof useWbChecksHistory>;
  onOpenCard: (c: CardRef) => void;
}): React.ReactElement {
  const showLegal = legal === WB_ALL;
  if (checks.isError) return <ErrorBox error={checks.error} what="проверки (/checks)" />;
  if (!checks.data) return <Loading text="Загружаю проверки…" />;
  const list = sortChecks(checks.data.checks);
  const order = list.map((c) => `${c.legal_name}|${c.check_code}`);
  return (
    <div className="space-y-4" data-testid="checks-tab">
      <div className="text-xs text-muted-foreground" data-testid="checks-latest">
        Текущее значение — за последний день агрегата проверки; «давний день» — раньше дня хранения (у
        проверок источников — раньше дня сборки):{" "}
        {Object.entries(checks.data.reference_dates)
          .map(
            ([l, d]) =>
              `${showLegal ? `${l}: ` : ""}день хранения ${fmtWhen(d.fbo_stock_date)}, сборка ${fmtWhen(d.build_day)}`,
          )
          .join(" · ") || "нет"}
      </div>
      {list.length === 0 ? (
        <div className="rounded-md border bg-muted/30 px-4 py-6 text-sm">У кабинета нет строк проверок в витрине.</div>
      ) : (
        <div className="overflow-x-auto rounded-md border">
          <table className="w-full text-xs" data-testid="checks-table">
            <thead className="bg-muted/40">
              <tr>
                <th className={cn(thClass, "text-left")}>Проверка</th>
                <th className={cn(thClass, "text-left")}>Статус</th>
                <th className={thClass}>Наше · Ожидалось · Δ / доля</th>
                <th
                  className={thClass}
                  title="Порог warn / fail: в % от ожидаемого; у свежести — дни; у покрытия продаж картой — нижняя граница доли"
                >
                  Порог warn / fail (ед.)
                </th>
                <th className={cn(thClass, "text-left")}>Дата</th>
                <th className={cn(thClass, "text-left")}>Детали</th>
              </tr>
            </thead>
            <tbody>
              {list.map((c) => (
                <CheckRow key={`${c.legal_name}|${c.check_code}`} c={c} showLegal={showLegal} onOpenCard={onOpenCard} />
              ))}
            </tbody>
          </table>
        </div>
      )}
      <section className="space-y-2">
        <h3 className="text-sm font-medium">История статусов по дням</h3>
        {history.isError ? (
          <ErrorBox error={history.error} what="историю проверок (/checks/history)" />
        ) : !history.data ? (
          <Loading text="Загружаю историю проверок…" />
        ) : (
          <HistoryStripView data={history.data} order={order} showLegal={showLegal} />
        )}
      </section>
    </div>
  );
}
